import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './content-navigation.module.scss';

export interface ContentNavigationProps {
  /** The name of the controls, e.g. the headline of the carousel */
  ariaLabel?: string;
  className?: string;
  currentIndex?: number;
  disableNext?: boolean;
  disablePrevious?: boolean;
  isPlaying?: boolean;
  /**
   * Whether the controls are only for pointer users, for strips where Tab
   * already reaches every item. They are hidden from keyboard and screen readers
   */
  isPointerOnly?: boolean;
  /** What the previous and next buttons move through */
  itemType?: 'image' | 'slide';
  onClickFullscreen?: () => void;
  onClickNext: () => void;
  onClickPrevious: () => void;
  onTogglePlay?: () => void;
  pageCount?: number;
  showPageCount?: boolean;
}

const ContentNavigation = ({
  ariaLabel,
  className,
  currentIndex,
  disableNext = false,
  disablePrevious = false,
  isPlaying = false,
  isPointerOnly = false,
  itemType = 'slide',
  pageCount,
  onClickFullscreen,
  onClickNext,
  onClickPrevious,
  onTogglePlay,
  showPageCount = false,
}: ContentNavigationProps) => {
  const { t } = useI18n();
  const [counter, setCounter] = useState('');

  const formatNumber = (value: number) => {
    return value < 10 ? `0${value}` : `${value}`;
  };

  useEffect(() => {
    if (typeof currentIndex !== 'number' || typeof pageCount !== 'number')
      return;

    setCounter(`${formatNumber(currentIndex + 1)}/${formatNumber(pageCount)}`);
  }, [currentIndex, pageCount]);

  return (
    <div
      aria-hidden={isPointerOnly || undefined}
      aria-label={ariaLabel ?? t('items.common.words.carouselControls')}
      className={classNames(styles.component, className)}
      role="group"
    >
      <Button
        aria-label={
          itemType === 'image'
            ? t('items.common.words.previousImage')
            : t('items.common.words.previousSlide')
        }
        disabled={disablePrevious}
        icon="ChevronLeft"
        onClick={onClickPrevious}
        tabIndex={isPointerOnly ? -1 : undefined}
        type="button"
        variant="tertiary"
      />

      {showPageCount ? (
        <React.Fragment>
          {/* "01/06" reads badly, so the visible counter is hidden from screen readers */}
          <Typography
            aria-hidden="true"
            className={styles.counter}
            tagStyle="labelMedium"
          >
            {counter}
          </Typography>

          <span aria-live="polite" className={styles.visuallyHidden}>
            {typeof currentIndex === 'number' &&
              typeof pageCount === 'number' &&
              t('items.common.words.slideCounter', {
                current: currentIndex + 1,
                total: pageCount,
              })}
          </span>
        </React.Fragment>
      ) : (
        onTogglePlay && (
          <Button
            aria-label={
              isPlaying
                ? t('items.common.words.pauseCarousel')
                : t('items.common.words.playCarousel')
            }
            icon={isPlaying ? 'Pause' : 'Play'}
            onClick={onTogglePlay}
            tabIndex={isPointerOnly ? -1 : undefined}
            type="button"
            variant="tertiary"
          />
        )
      )}

      <Button
        aria-label={
          itemType === 'image'
            ? t('items.common.words.nextImage')
            : t('items.common.words.nextSlide')
        }
        disabled={disableNext}
        icon="ChevronRight"
        onClick={onClickNext}
        tabIndex={isPointerOnly ? -1 : undefined}
        type="button"
        variant="tertiary"
      />

      {onClickFullscreen && (
        <Button
          aria-label={t('items.common.words.fullScreen')}
          icon="Expand"
          onClick={onClickFullscreen}
          tabIndex={isPointerOnly ? -1 : undefined}
          type="button"
          variant="tertiary"
        />
      )}
    </div>
  );
};

ContentNavigation.displayName = 'ContentNavigation';

export default ContentNavigation;
